// Pure decision for the terminal-run refetch gate in App's terminal-sync
// effect. Kept side-effect-free so the once-per-termination policy is
// unit-testable without rendering <App/>.

/**
 * Select the terminal runs that have not been synced yet.
 *
 * The event-driven run-status SSE stream re-fires the terminal-sync
 * effect on every frame; while a finished run lingers in ``activeRuns``
 * next to a still-streaming one, an ungated refetch would fire per
 * frame. This returns the `${runId}:${status}` keys of terminal runs
 * absent from ``syncedKeys`` so the caller refetches once per genuine
 * termination — a run re-terminating with a *different* status (e.g.
 * failed → cancelled) still produces a new key and refetches again.
 *
 * @param {Array<{runId: string, status: string}>} terminalRuns - Runs
 *   currently in a terminal state (completed/failed/cancelled).
 * @param {Set<string>} syncedKeys - Previously synced
 *   `${runId}:${status}` keys. Not mutated; the caller records the
 *   returned keys after acting on them.
 * @returns {string[]} Keys of runs that should trigger a refetch.
 */
export function selectUnsyncedTerminalRunKeys(terminalRuns, syncedKeys) {
  return terminalRuns
    .map((r) => `${r.runId}:${r.status}`)
    .filter((key) => !syncedKeys.has(key));
}
